import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, cn, formatDateTime } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, FileEdit, Loader2,
  RefreshCw, Filter,
} from "lucide-react";

export default function OrderEditRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [reviewDialog, setReviewDialog] = useState(null); // holds request_id
  const [reviewNote, setReviewNote] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `${API}/order-edit-requests`;
      if (statusFilter && statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      const res = await axios.get(url, { withCredentials: true });
      setRequests(res.data);
    } catch (err) {
      toast.error("Failed to load edit requests");
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (requestId, approved) => {
    setUpdating(true);
    try {
      await axios.post(
        `${API}/order-edit-requests/${requestId}/approve`,
        { approved, review_note: reviewNote },
        { withCredentials: true }
      );
      toast.success(approved ? "Edit request approved and changes applied" : "Edit request rejected");
      setReviewDialog(null);
      setReviewNote("");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to review request");
    } finally {
      setUpdating(false);
    }
  };

  const isSuperAdmin = user?.role === "super_admin";

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-[300px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-edit-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Order Edit Requests</h1>
          <p className="text-muted-foreground mt-1">
            {isSuperAdmin ? "Review and approve order modifications" : "Track your order edit requests"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] rounded-sm h-9" data-testid="edit-request-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchRequests} className="rounded-sm" data-testid="refresh-edit-requests">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {requests.length === 0 ? (
        <Card className="border border-border shadow-none rounded-sm">
          <CardContent className="py-16 text-center text-muted-foreground">
            <FileEdit className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="font-medium">No edit requests found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => {
            const isPending = req.status === "pending";
            const isApproved = req.status === "approved";
            return (
              <Card key={req.request_id} className="border border-border shadow-none rounded-sm" data-testid={`edit-request-${req.request_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium">{req.order_number}</span>
                        <Badge
                          variant={isPending ? "outline" : isApproved ? "default" : "destructive"}
                          className={cn(
                            "text-xs",
                            isPending && "border-amber-300 text-amber-600 bg-amber-50",
                            isApproved && "bg-emerald-100 text-emerald-700 border-emerald-300"
                          )}
                        >
                          {req.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Requested by <strong>{req.requested_by_name}</strong> on {formatDateTime(req.created_at)}
                      </p>
                      {req.reason && (
                        <p className="text-sm"><span className="text-muted-foreground">Reason:</span> {req.reason}</p>
                      )}
                      <div className="mt-2">
                        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Proposed Changes</p>
                        <div className="space-y-1">
                          {Object.entries(req.changes || {}).map(([field, value]) => (
                            <div key={field} className="flex items-center gap-2 text-sm">
                              <Badge variant="outline" className="text-[10px] font-mono">{field}</Badge>
                              <span className="text-muted-foreground">&rarr;</span>
                              <span>{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {req.review_note && (
                        <p className="text-sm mt-2 text-muted-foreground">
                          Review note: <em>{req.review_note}</em>
                        </p>
                      )}
                    </div>
                    {isSuperAdmin && isPending && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="rounded-sm bg-emerald-600 hover:bg-emerald-700 h-8"
                          onClick={() => setReviewDialog({ id: req.request_id, action: "approve" })}
                          data-testid={`approve-edit-${req.request_id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="rounded-sm h-8"
                          onClick={() => setReviewDialog({ id: req.request_id, action: "reject" })}
                          data-testid={`reject-edit-${req.request_id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={(open) => { if (!open) { setReviewDialog(null); setReviewNote(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog?.action === "approve" ? "Approve Edit Request" : "Reject Edit Request"}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog?.action === "approve"
                ? "Approving will apply the proposed changes to the order."
                : "Rejecting will discard the proposed changes."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Add a review note (optional)..."
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            className="rounded-sm"
            data-testid="review-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewDialog(null); setReviewNote(""); }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleReview(reviewDialog?.id, reviewDialog?.action === "approve")}
              disabled={updating}
              className={cn(
                "rounded-sm",
                reviewDialog?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-destructive hover:bg-destructive/90"
              )}
              data-testid="confirm-review-button"
            >
              {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {reviewDialog?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
